import {
  DIRECT_MATCH_BONUS,
  getBaseLeafScore,
  getBaseParentScore,
  getPositionBonusAnyOccurrence,
  getSuggestedMatchQuality,
  SUGGESTED_POSITION_MULTIPLIER,
} from '../../../selection-lists/utils/findSelectionListItemByLabel';
import {
  getMatchQuality,
  MatchQuality,
} from '../../../selection-lists/utils/match-quality';
import type { TreeData } from '../useTreeApi';
import { resolveSuggestedTitles } from './normalizeSuggestedTitles';

type ScoredNode = [node: TreeData, score: number, hasExact: boolean];

const exactMatchSort = (items: ScoredNode[]): ScoredNode[] =>
  [...items].sort(([, a, aExact], [, b, bExact]) => {
    if (aExact !== bExact) return aExact ? -1 : 1;
    return b - a;
  });

type NodeType = 'leaf' | 'parent';

const getNodeScore = (node: TreeData, search: string): number => {
  const suggestedTitleStrings = resolveSuggestedTitles(node) ?? [];
  const directQuality = getMatchQuality(node.name, search);
  const suggestedQuality = getSuggestedMatchQuality(
    suggestedTitleStrings,
    search
  );

  const nodeType: NodeType = node.children?.length ? 'parent' : 'leaf';

  // Calculate base scores
  const directScore =
    nodeType === 'leaf'
      ? getBaseLeafScore('direct', directQuality)
      : getBaseParentScore('direct', directQuality);
  let suggestedScore = 0;
  if (directQuality === MatchQuality.NONE) {
    suggestedScore =
      nodeType === 'leaf'
        ? getBaseLeafScore('suggested', suggestedQuality)
        : getBaseParentScore('suggested', suggestedQuality);
  }

  let totalScore = directScore + suggestedScore;

  // Add massive bonus for any direct match to ensure they rank above all suggested matches
  if (directQuality !== MatchQuality.NONE) {
    totalScore += DIRECT_MATCH_BONUS;
  }

  // Add position bonuses
  if (directQuality !== MatchQuality.NONE) {
    totalScore += getPositionBonusAnyOccurrence(node.name, search);
  } else if (suggestedQuality !== MatchQuality.NONE) {
    const maxSuggestedBonus = Math.max(
      ...suggestedTitleStrings.map((title) =>
        getPositionBonusAnyOccurrence(title, search)
      )
    );
    totalScore += Math.floor(maxSuggestedBonus * SUGGESTED_POSITION_MULTIPLIER);
  }

  return totalScore;
};

export const sortTreeBySearchRelevance = (
  data: TreeData[],
  search: string
): TreeData[] => {
  if (!search?.trim()) return data;

  const processNode = (node: TreeData): ScoredNode => {
    const ownScore = getNodeScore(node, search);
    const ownExact = getMatchQuality(node.name, search) === MatchQuality.EXACT;

    if (!node.children?.length) {
      return [node, ownScore, ownExact];
    }

    const processedChildren = exactMatchSort(node.children.map(processNode));

    const childrenScore = processedChildren.reduce(
      (sum, [, score]) => sum + score,
      0
    );
    const anyChildExact = processedChildren.some(([, , exact]) => exact);

    return [
      { ...node, children: processedChildren.map(([child]) => child) },
      ownScore + childrenScore,
      ownExact || anyChildExact,
    ];
  };

  return exactMatchSort(data.map(processNode)).map(([node]) => node);
};

export const sortSelectedFirst = (
  data: TreeData[],
  selectedIds: string[],
  indeterminateIds: string[]
): TreeData[] => {
  const isMarked = (id: string) =>
    selectedIds.includes(id) || indeterminateIds.includes(id);

  return [...data].sort((a, b) => {
    const aMarked = isMarked(a.id);
    const bMarked = isMarked(b.id);
    if (aMarked !== bMarked) {
      return aMarked ? -1 : 1;
    }

    const aIsOther = a.name === 'Other';
    const bIsOther = b.name === 'Other';
    if (aIsOther !== bIsOther) {
      return aIsOther ? 1 : -1;
    }

    return data.indexOf(a) - data.indexOf(b);
  });
};
